import { memo, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import MessageForm, { type MessageFormProps } from "./MessageForm";

type Props = MessageFormProps & {
  open: boolean;
  onClose: () => void;
  title: string;
  intro?: React.ReactNode;
};

/** MessageForm in a dialog; full-screen on phones. */
const MessageDialog = memo(({ open, onClose, title, intro, ...formProps }: Props) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // Remount the form on every open so a sent dialog reopens fresh.
  const [openCount, setOpenCount] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOpenCount((n) => n + 1);
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="sm" aria-labelledby="message-dialog-title">
      <DialogTitle id="message-dialog-title" sx={{ pr: 7, fontWeight: 700 }}>
        {title}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "4px !important" }}>
        {intro && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {intro}
          </Typography>
        )}
        <MessageForm
          key={openCount}
          {...formProps}
          successAction={
            <Button variant="contained" onClick={onClose} sx={{ textTransform: "none" }}>
              Done
            </Button>
          }
        />
      </DialogContent>
    </Dialog>
  );
});

export default MessageDialog;
